import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { agentApi } from "../api/client";
import { createLogger } from "../utils/logger";

const log = createLogger("AgentCtx");
const AgentContext = createContext(null);

export function AgentProvider({ children }) {
  const [agents, setAgents] = useState([]);
  const [activeAgent, setActiveAgent] = useState(null);
  const [agentStatus, setAgentStatus] = useState("draft");

  const fetchAgents = useCallback(async () => {
    try {
      const res = await agentApi.list();
      if (res.success) {
        setAgents(res.data || []);
        if (!activeAgent && res.data?.length > 0) {
          setActiveAgent(res.data[0].id);
          setAgentStatus(res.data[0].status);
        }
      }
    } catch (err) {
      log.error("Failed to fetch agents", err);
    }
  }, [activeAgent]);

  useEffect(() => { fetchAgents(); }, []);

  const currentAgent = agents.find(a => a.id === activeAgent) || agents[0] || {
    id: null, name: "No Agents", version: "", status: "draft",
    channels: [], model: "\u2014", temperature: 0, max_tokens: 0,
  };

  const handleCloneAgent = async (sourceAgent) => {
    // B51 (2026-07-23): server-side clone. The old client-side copy via
    // agentApi.create silently DROPPED flow_config (toolsets, knowledge
    // config, supervisor config — the agent's brain), provider, and
    // template fields, and copied metadata_json verbatim (leaking
    // bootstrap-only platform flags). POST /api/agents/{id}/clone does
    // the complete copy with flag-stripping and an audit trail.
    try {
      const res = await agentApi.clone(sourceAgent.id);
      if (res.success) {
        await fetchAgents();
        setActiveAgent(res.data.id);
        setAgentStatus("draft");
      }
      return res;
    } catch (err) { return { success: false, error: err.message }; }
  };

  const handleAgentStatusChange = async (newStatus) => {
    const agentId = currentAgent?.id;
    setAgentStatus(newStatus);
    if (!agentId) return;
    try {
      const res = await agentApi.updateStatus(agentId, newStatus);
      if (res.success) {
        setAgents(prev => prev.map(a => a.id === agentId ? { ...a, status: newStatus } : a));
      }
    } catch (err) {
      log.error("Status update failed", err);
    }
  };

  const getAgentDependencies = useCallback((agentId) => {
    const agent = agents.find(a => a.id === agentId);
    if (!agent?.flow_config?.orchestration?.steps) return [];
    return agent.flow_config.orchestration.steps
      .map(s => s.agentId)
      .filter(Boolean);
  }, [agents]);

  const hasCircularDependency = useCallback((parentId, childId) => {
    const visited = new Set();
    const check = (id) => {
      if (id === parentId) return true;
      if (visited.has(id)) return false;
      visited.add(id);
      return getAgentDependencies(id).some(check);
    };
    return check(childId);
  }, [getAgentDependencies]);

  return (
    <AgentContext.Provider value={{
      agents, activeAgent, setActiveAgent,
      agentStatus, setAgentStatus, currentAgent,
      fetchAgents, handleCloneAgent, handleAgentStatusChange,
      getAgentDependencies, hasCircularDependency,
    }}>
      {children}
    </AgentContext.Provider>
  );
}

export function useAgentContext() {
  const ctx = useContext(AgentContext);
  if (!ctx) throw new Error("useAgentContext must be inside AgentProvider");
  return ctx;
}
